import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { get, post } from '../utils/api';
import MarkingGuidePanel from '../components/MarkingGuidePanel';

function confidenceLabel(score) {
  if (score >= 0.85) return { text: 'High', className: 'confidence-high' };
  if (score >= 0.6) return { text: 'Medium', className: 'confidence-medium' };
  return { text: 'Low', className: 'confidence-low' };
}

function recommendationBadge(rec) {
  const map = {
    competent: ['Competent', 'rec-competent'],
    not_yet_competent: ['Not Yet Competent', 'rec-nyc'],
    resubmit: ['Resubmit Required', 'rec-resubmit'],
    review_needed: ['Manual Review Needed', 'rec-review']
  };
  const [label, cls] = map[rec] || [rec, ''];
  return <span className={`rec-badge ${cls}`}>{label}</span>;
}

function severityIcon(severity) {
  if (severity === 'concern') return '⚠️';
  if (severity === 'note') return '📝';
  return 'ℹ️';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// --- Copy-for-Canvas helpers -------------------------------------------------
// Split a block of prose into individual sentences so each can sit on its own
// line with a blank line between, the way Paul lays feedback out in Canvas.
function splitSentences(text) {
  if (!text) return [];
  return String(text)
    .replace(/\s+/g, ' ')
    .trim()
    // Break after . ! or ? when followed by a space and the start of a new
    // sentence (a capital letter, quote or opening bracket).
    .split(/(?<=[.!?])\s+(?=[A-Z"'(“‘])/)
    .map(s => s.trim())
    .filter(Boolean);
}

// Build a plain-text version of the feedback, already spaced for pasting
// straight into the Canvas comment box. Each sentence and each bullet is
// separated by a blank line, and the two headings have blank lines around them.
function buildCanvasFeedback(review) {
  if (!review) return '';
  const blocks = [];

  const sentences = splitSentences(review.overall_comment);
  if (sentences.length) blocks.push(sentences.join('\n\n'));

  if (review.strengths && review.strengths.length) {
    blocks.push('Things You Did Well');
    blocks.push(review.strengths.map(s => `• ${String(s).trim()}`).join('\n\n'));
  }

  if (review.improvements && review.improvements.length) {
    blocks.push('Areas for Development');
    blocks.push(review.improvements.map(s => `• ${String(s).trim()}`).join('\n\n'));
  }

  return blocks.join('\n\n');
}
// ----------------------------------------------------------------------------

export default function SubmissionDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDecisionForm, setShowDecisionForm] = useState(false);
  const [decision, setDecision] = useState({
    agreed_with_ai: true,
    final_grade: 'competent',
    final_score: '',
    assessor_notes: '',
    time_spent_mins: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Re-run AI review state
  const [rerunning, setRerunning] = useState(false);
  const [rerunMessage, setRerunMessage] = useState(null);

  // Copy-for-Canvas feedback state
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get(`/submissions/${id}`);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleCheckout = async () => {
    try {
      await post(`/submissions/${id}/checkout`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRelease = async () => {
    try {
      await post(`/submissions/${id}/release`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRerunAi = async () => {
    if (!window.confirm('This will delete the current AI review and generate a fresh one. This may take up to 30 seconds. Continue?')) {
      return;
    }
    setRerunning(true);
    setRerunMessage(null);
    try {
      const result = await post(`/submissions/${id}/rerun-ai`);
      setRerunMessage({ type: 'success', text: `AI review re-run complete: ${(result.recommendation || '').replace(/_/g, ' ')}` });
      load(); // Refresh the page data
    } catch (err) {
      setRerunMessage({ type: 'error', text: err.message || 'Failed to re-run AI review' });
    } finally {
      setRerunning(false);
    }
  };

  const handleCopyForCanvas = async (review) => {
    const text = buildCanvasFeedback(review);
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (err) {
      // Fallback for older browsers or non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSubmitDecision = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const latestReview = data.ai_reviews && data.ai_reviews.length > 0 ? data.ai_reviews[0] : null;
      await post(`/submissions/${id}/decision`, {
        ai_review_id: latestReview ? latestReview.id : null,
        agreed_with_ai: decision.agreed_with_ai,
        final_grade: decision.final_grade,
        final_score: decision.final_score ? parseFloat(decision.final_score) : null,
        assessor_notes: decision.assessor_notes,
        time_spent_mins: decision.time_spent_mins ? parseInt(decision.time_spent_mins) : null
      });
      setShowDecisionForm(false);
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading submission...</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#DC2626' }}>Error: {error}</div>;
  if (!data) return null;

  const sub = data.submission;
  const reviews = data.ai_reviews || [];
  const decisions = data.decisions || [];
  const latestReview = reviews.length > 0 ? reviews[0] : null;
  const isCheckedOutByMe = sub.status === 'checked_out' && sub.checked_out_by_name === user.name;
  const canRerunAi = data.can_rerun_ai === true;

  return (
    <div className="detail-page">
      {/* Back nav */}
      <button className="btn btn-secondary btn-sm" onClick={() => navigate('/')} style={{ marginBottom: 16 }}>
        &larr; Back to Queue
      </button>

      {/* Submission header */}
      <div className="card detail-header">
        <div className="detail-header-top">
          <div>
            <h1 className="detail-title">{sub.assignment_name}</h1>
            <div className="detail-meta">
              <span className="meta-item"><strong>Student:</strong> {sub.student_name}</span>
              <span className="meta-item"><strong>Course:</strong> {sub.course_name}</span>
              <span className="meta-item"><strong>Submitted:</strong> {formatDate(sub.submitted_at)}</span>
              <span className="meta-item"><strong>Attempt:</strong> {sub.attempt || 1}</span>
            </div>
          </div>
          <div className="detail-actions">
            {(sub.status === 'submitted' || sub.status === 'ai_reviewed') && (
              <button className="btn btn-primary" onClick={handleCheckout}>Check Out</button>
            )}
            {isCheckedOutByMe && (
              <>
                <button className="btn btn-accent" onClick={() => setShowDecisionForm(true)}>Record Decision</button>
                <button className="btn btn-secondary" onClick={handleRelease}>Release</button>
              </>
            )}
            <a href={sub.speedgrader_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              Open SpeedGrader
            </a>
          </div>
        </div>
      </div>

      {/* Batch 22: hidden marking guide for this task, beside the AI review */}
      {data.marking_guide && (
        <MarkingGuidePanel guide={data.marking_guide} cot={/\(COT\)|Certificate of Training/i.test(sub.course_name || '')} />
      )}

      {/* AI Review Section */}
      {latestReview ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>AI Preliminary Review</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: '#9CA3AF' }}>
                {formatDate(latestReview.created_at)} &middot; {latestReview.model_used}
              </span>
              {canRerunAi && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleRerunAi}
                  disabled={rerunning}
                  style={{ fontSize: 12, padding: '4px 10px' }}
                >
                  {rerunning ? 'Re-running...' : 'Re-run AI Review'}
                </button>
              )}
            </div>
          </div>

          {/* Copy feedback for Canvas */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 16px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleCopyForCanvas(latestReview)}
              title="Copies the preliminary review, Things You Did Well and Areas for Development to your clipboard, already spaced for pasting into the Canvas comment box."
            >
              {copied ? '✓ Copied — now paste into Canvas' : '📋 Copy feedback for Canvas'}
            </button>
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>
              One click, then paste into the Canvas comment box — already spaced out.
            </span>
          </div>

          {/* Re-run feedback message */}
          {rerunMessage && (
            <div style={{
              padding: '8px 14px',
              marginBottom: 12,
              borderRadius: 6,
              fontSize: 13,
              backgroundColor: rerunMessage.type === 'success' ? '#ECFDF5' : '#FEF2F2',
              color: rerunMessage.type === 'success' ? '#065F46' : '#991B1B',
              border: `1px solid ${rerunMessage.type === 'success' ? '#A7F3D0' : '#FECACA'}`
            }}>
              {rerunMessage.text}
            </div>
          )}

          {/* Recommendation + Confidence */}
          <div className="review-summary">
            <div className="review-recommendation">
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>Recommendation</div>
              {recommendationBadge(latestReview.recommendation)}
            </div>
            <div className="review-confidence">
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>Confidence</div>
              <div className="confidence-bar-container">
                <div className={`confidence-bar ${confidenceLabel(latestReview.confidence).className}`}
                     style={{ width: `${(latestReview.confidence * 100)}%` }} />
              </div>
              <span className={`confidence-text ${confidenceLabel(latestReview.confidence).className}`}>
                {Math.round(latestReview.confidence * 100)}% — {confidenceLabel(latestReview.confidence).text}
              </span>
            </div>
            {latestReview.ai_engagement_score != null && (
              <div className="review-engagement">
                <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>Learning Engagement</div>
                <div className="confidence-bar-container">
                  <div className="confidence-bar confidence-high"
                       style={{ width: `${(latestReview.ai_engagement_score * 100)}%`, background: '#00707F' }} />
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#003040' }}>
                  {Math.round(latestReview.ai_engagement_score * 100)}%
                </span>
              </div>
            )}
          </div>

          {/* Overall Comment */}
          {latestReview.overall_comment && (
            <div className="review-comment">
              {splitSentences(latestReview.overall_comment).map((sentence, i) => (
                <p key={i} style={{ marginBottom: 12 }}>{sentence}</p>
              ))}
            </div>
          )}

          {/* Strengths and Improvements */}
          <div className="review-columns">
            {latestReview.strengths && latestReview.strengths.length > 0 && (
              <div className="review-col">
                <h3 className="col-heading col-strengths">Things You Did Well</h3>
                <ul className="review-list">
                  {latestReview.strengths.map((s, i) => (
                    <li key={i} className="review-list-item strength-item" style={{ marginBottom: 10 }}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {latestReview.improvements && latestReview.improvements.length > 0 && (
              <div className="review-col">
                <h3 className="col-heading col-improvements">Areas for Development</h3>
                <ul className="review-list">
                  {latestReview.improvements.map((s, i) => (
                    <li key={i} className="review-list-item improvement-item" style={{ marginBottom: 10 }}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Rubric Scores */}
          {latestReview.rubric_scores && Object.keys(latestReview.rubric_scores).length > 0 && (
            <div className="rubric-section">
              <h3>Rubric Assessment</h3>
              <table className="data-table rubric-table">
                <thead>
                  <tr>
                    <th>Criterion</th>
                    <th>Result</th>
                    <th>AI Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(latestReview.rubric_scores).map(([criterion, detail], i) => (
                    <tr key={i}>
                      <td><strong>{criterion}</strong></td>
                      <td>
                        {typeof detail === 'object' && detail.met != null ? (
                          <span className={`badge ${detail.met ? 'badge-graded' : 'badge-overdue'}`}>
                            {detail.met ? 'Satisfactory' : 'Not Yet Satisfactory'}
                          </span>
                        ) : (
                          <span className="badge badge-pending">Pending</span>
                        )}
                      </td>
                      <td style={{ fontSize: 13, color: '#4B5563' }}>
                        {typeof detail === 'object' && detail.comment ? detail.comment : (typeof detail === 'string' ? detail : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* AI Usage Flags */}
          {latestReview.ai_usage_flags && latestReview.ai_usage_flags.length > 0 && (
            <div className="ai-flags-section">
              <h3>AI Usage Analysis</h3>
              <div className="ai-flags-list">
                {latestReview.ai_usage_flags.map((flag, i) => (
                  <div key={i} className={`ai-flag ai-flag-${flag.severity || 'info'}`}>
                    <span className="flag-icon">{severityIcon(flag.severity)}</span>
                    <div className="flag-content">
                      <div className="flag-label">{flag.label || flag.type || 'Flag'}</div>
                      <div className="flag-detail">{flag.detail || flag.description || ''}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="ai-flags-note">
                AI usage indicators are for developmental purposes only. They help identify where students may benefit from additional support in developing their own analytical voice.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="card" style={{ marginTop: 16, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#9CA3AF' }}>No AI review available for this submission yet.</span>
            {canRerunAi && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleRerunAi}
                disabled={rerunning}
              >
                {rerunning ? 'Running AI Review...' : 'Run AI Review'}
              </button>
            )}
          </div>
          {rerunMessage && (
            <div style={{
              padding: '8px 14px',
              marginTop: 12,
              borderRadius: 6,
              fontSize: 13,
              backgroundColor: rerunMessage.type === 'success' ? '#ECFDF5' : '#FEF2F2',
              color: rerunMessage.type === 'success' ? '#065F46' : '#991B1B',
              border: `1px solid ${rerunMessage.type === 'success' ? '#A7F3D0' : '#FECACA'}`
            }}>
              {rerunMessage.text}
            </div>
          )}
        </div>
      )}

      {/* Previous Assessor Decisions */}
      {decisions.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#003040', marginBottom: 16 }}>Assessor Decisions</h2>
          {decisions.map((dec, i) => (
            <div key={i} className="decision-record">
              <div className="decision-header">
                <strong>{dec.assessor_name}</strong>
                <span style={{ color: '#9CA3AF', fontSize: 13 }}>{formatDate(dec.created_at)}</span>
              </div>
              <div className="decision-details">
                <span><strong>Grade:</strong> {dec.final_grade}</span>
                {dec.final_score != null && <span><strong>Score:</strong> {dec.final_score}</span>}
                {dec.agreed_with_ai != null && (
                  <span>
                    <strong>Agreed with AI:</strong>{' '}
                    <span className={`badge ${dec.agreed_with_ai ? 'badge-graded' : 'badge-overdue'}`}>
                      {dec.agreed_with_ai ? 'Yes' : 'No'}
                    </span>
                  </span>
                )}
                {dec.time_spent_mins && <span><strong>Time spent:</strong> {dec.time_spent_mins} mins</span>}
              </div>
              {dec.assessor_notes && <p className="decision-notes">{dec.assessor_notes}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Decision Form Modal */}
      {showDecisionForm && (
        <div className="modal-overlay" onClick={() => setShowDecisionForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: 16, color: '#003040' }}>Record Assessment Decision</h2>
            <form onSubmit={handleSubmitDecision}>
              <div className="form-group">
                <label>Final Grade</label>
                <select value={decision.final_grade}
                        onChange={e => setDecision({ ...decision, final_grade: e.target.value })}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}>
                  <option value="competent">Competent</option>
                  <option value="not_yet_competent">Not Yet Competent</option>
                  <option value="resubmit">Resubmit Required</option>
                </select>
              </div>
              <div className="form-group">
                <label>Score (optional)</label>
                <input type="number" step="0.1" value={decision.final_score}
                       onChange={e => setDecision({ ...decision, final_score: e.target.value })}
                       placeholder="e.g. 85" />
              </div>
              {latestReview && (
                <div className="form-group">
                  <label>
                    <input type="checkbox" checked={decision.agreed_with_ai}
                           onChange={e => setDecision({ ...decision, agreed_with_ai: e.target.checked })}
                           style={{ marginRight: 8 }} />
                    I agree with the AI recommendation ({latestReview.recommendation.replace(/_/g, ' ')})
                  </label>
                </div>
              )}
              <div className="form-group">
                <label>Assessor Notes</label>
                <textarea value={decision.assessor_notes}
                          onChange={e => setDecision({ ...decision, assessor_notes: e.target.value })}
                          rows={4} placeholder="Your assessment feedback and observations..."
                          style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label>Time Spent (minutes)</label>
                <input type="number" value={decision.time_spent_mins}
                       onChange={e => setDecision({ ...decision, time_spent_mins: e.target.value })}
                       placeholder="e.g. 15" />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowDecisionForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Decision'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
